import {
  NamesTabGroup,
  NamesTabSource,
} from '../../../components/modals/fields/NamesTab';
import { ProductNameVariant } from '../../../services/api/domains/products/types';
import { normalizeName } from './resolvePreviewSource';

// Names tab: roles with a real owner concept (issue #622, Sub-task 10.1) —
// null for cas_official/preferred/cas_synonym, which have no owner concept.
// Only these roles' groups render an owner list at all; other roles just
// show their source label with no owners row.
const OWNED_NAME_ROLES = new Set([
  'orange_book_ingredient',
  'ndc_generic',
  'dmf_subject',
  'manufacturer',
]);

// The manufacturer role's per-variant `label` is the manufacturer's own
// company name (api/sql/products.js getProductNameVariants), not a generic
// source descriptor — every manufacturer link would otherwise become its
// own "source" bucket instead of one "Manufacturer" bucket listing every
// manufacturer as an owner. Overridden here to the same generic label
// api/constants/products.js NAME_ROLE_LABELS.manufacturer uses server-side
// (can't import that CommonJS constant from the client package).
const MANUFACTURER_SOURCE_LABEL = 'Manufacturer product name';

// Explicit empty-owner indicator — matches the '—' convention already used
// for missing fields elsewhere (OrangeBookPanel.tsx, DMFPanel.tsx,
// CASRegistryPanel.tsx), rather than rendering blank or the literal "null".
const MISSING_OWNER = '—';

/**
 * Collapses the ranked variant list to one option per distinct normalized
 * name (issue #622, Sub-task 10.1), listing every source and every owner
 * within each source. Preserves the backend's rank/synonym ordering — a
 * group's position is set by its first-encountered variant, and `variants`
 * is already rank-ascending with synonyms last (Requirement 3).
 *
 * Returns the generic `NamesTabGroup` shape the shared
 * `components/modals/fields/NamesTab.tsx` component renders — moved here
 * (out of the old SynonymsRadioField.tsx) when the Names tab UI was
 * extracted into that shared component (issue #653, Task 16 QA follow-up).
 */
export function groupVariantsByName(
  variants: ProductNameVariant[]
): NamesTabGroup[] {
  const groups = new Map<string, NamesTabGroup>();
  const order: string[] = [];

  variants.forEach(variant => {
    const key = normalizeName(variant.name);
    let group = groups.get(key);
    if (!group) {
      group = { key, name: variant.name, sources: [] };
      groups.set(key, group);
      order.push(key);
    }

    const sourceLabel =
      variant.name_role === 'manufacturer'
        ? MANUFACTURER_SOURCE_LABEL
        : variant.label;

    let source: NamesTabSource | undefined = group.sources.find(
      s => s.label === sourceLabel
    );
    if (!source) {
      source = {
        label: sourceLabel,
        owners: OWNED_NAME_ROLES.has(variant.name_role) ? [] : null,
      };
      group.sources.push(source);
    }

    if (source.owners) {
      const ownerText = variant.owner ?? MISSING_OWNER;
      if (!source.owners.includes(ownerText)) {
        source.owners.push(ownerText);
      }
    }
  });

  return order.map(key => groups.get(key) as NamesTabGroup);
}
